import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Link,
} from "@mui/material";
import sectionService from "../../../services/Sections/Home/sectionService";
import formatLanguage from "../../../utils/formatLanguage";
import SectionCardSkeleton from "../../Sections/components/SectionCardSkeleton";
import { baseURL } from "../../../services/apiClient";

const formatDescriptionPreview = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}...`;
};

// Utility function to process image URLs
const processImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // If it's already an absolute URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }
  
  // If it's a relative URL, prepend the base URL
  // Get the base domain from the API URL (remove /api/v1)
  const apiBaseUrl = baseURL.replace("/api/v1", "").replace(/\/$/, ""); // Remove /api/v1 and trailing slash
  
  // If imageUrl starts with "/", it's an absolute path from domain root
  if (imageUrl.startsWith("/")) {
    return `${apiBaseUrl}${imageUrl}`;
  }
  
  // Otherwise, it's a relative path
  return `${apiBaseUrl}/${imageUrl}`;
};

class SectionCardIdentityManager {
  static slugFromName(name) {
    if (typeof name !== "string") {
      return "";
    }
    return name.trim().toLowerCase().replace(/\s+/g, "-");
  }

  static normalizeCollection(cards = []) {
    return cards.map((card, index) => {
      const slug = this.slugFromName(card?.section ?? "");
      const derivedId = card?.id ?? (slug ? `card-${slug}` : `card-${index}`);

      return {
        ...card,
        id: derivedId,
        slug,
      };
    });
  }
}

const DefaultCard = ({ sx }) => (
  <Box
    sx={{
      ...sx,
      p: { xs: 2.5, sm: 3 },
      borderRadius: 2,
      backgroundColor: "#F8FAFC",
      boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{ color: "#06B6D4", fontWeight: "bold" }}
    >
      EXPLORE
    </Typography>
    <Typography
      variant="h3"
      sx={{
        fontWeight: "bold",
        color: "#111827",
        fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
        lineHeight: 1.2,
      }}
    >
      Maharashtra's General Administration Department.
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: "#4B5563", fontSize: { xs: "0.9rem", md: "1rem" } }}
    >
      Learn more about each subject of General Administrative Department
    </Typography>
    <Link
      href="#"
      sx={{
        color: "#06B6D4",
        fontWeight: "bold",
        textDecoration: "none",
        marginTop: { xs: 2, md: 1 },
        "&:hover": { textDecoration: "underline" },
        fontSize: { xs: "0.5rem", md: "0.8rem" },
      }}
    >
      View all subjects →
    </Link>
  </Box>
);

const SectionCard = ({ imageSrc, sectionTitle, description, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);

  useEffect(() => {
    if (imageSrc) {
      const processed = processImageUrl(imageSrc);
      setProcessedImageUrl(processed);
      setImageError(false);
    } else {
      setProcessedImageUrl(null);
      setImageError(true);
    }
  }, [imageSrc]);

  const defaultBackgroundColor = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";

  return (
    <Box
      onClick={onClick}
      sx={{
        position: "relative",
        height: { xs: 220, sm: 240, md: 270 },
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        backgroundImage: imageError || !processedImageUrl
          ? defaultBackgroundColor 
          : `url(${processedImageUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "all 0.5s ease",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover .circularOverlay": {
          transform: "translate(-50%, -50%) scale(1)",
        },
        "&:hover .title": {
          top: "10%",
          transform: "translateX(-50%)",
          fontSize: { xs: "1.35rem", sm: "1.4rem", md: "1.5rem" },
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        "&:hover .description": {
          opacity: 1,
          display: "-webkit-box",
        },
      }}
    >
      {/* Hidden img element for error handling on background images */}
      {processedImageUrl && !imageError && (
        <Box
          component="img"
          src={processedImageUrl}
          alt=""
          onError={() => setImageError(true)}
          sx={{
            position: "absolute",
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: "none",
            zIndex: -1,
          }}
        />
      )}
      {/* Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.4)",
          zIndex: 0,
        }}
      />

      {/* Circular Overlay */}
      <Box
        className="circularOverlay"
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: { xs: 260, sm: 360, md: 500 },
          height: { xs: 260, sm: 360, md: 500 },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          borderRadius: "50%",
          transform: "translate(-50%, -50%) scale(0)",
          transition: "transform 0.5s ease",
          zIndex: 1,
        }}
      />

      {/* Title */}
      <Typography
        variant="h4"
        className="title"
        sx={{
          color: "#fff",
          fontWeight: "bold",
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          transition: "all 0.5s ease",
          fontSize: { xs: "1.6rem", sm: "2rem", md: "2.5rem" },
          zIndex: 2,
          textAlign: "center",
          px: { xs: 2, sm: 1 },
        }}
      >
        {sectionTitle}
      </Typography>

      {/* Description */}
      <Typography
        variant="body1"
        className="description"
        sx={{
          color: "#fff",
          position: "absolute",
          left: { xs: 12, sm: 16 },
          right: { xs: 12, sm: 16 },
          top: "50%",
          transform: "translateY(-50%)",
          opacity: 0,
          transition: "opacity 0.5s ease",
          textAlign: "center",
          display: { xs: "none", md: "-webkit-box" },
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          zIndex: 2,
        }}
      >
        {description}
      </Typography>

      {/* Learn More */}
      <Box
        sx={{
          position: "absolute",
          bottom: 10,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          px: { xs: 1, md: 1.5 },
          py: { xs: 0.5, md: 0.75 },
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" sx={{ color: "#fff" }}>
          Learn More
        </Typography>
      </Box>
    </Box>
  );
};

const SectionCardsDisplay = () => {
  const selectedLanguage = useSelector(
    (state) => state.language.selectedLanguage || "English"
  );
  const [sectionCards, setSectionCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cards = await sectionService.fetchSectionCardsWithLanguage(
          formatLanguage(selectedLanguage)
        );
        const normalizedCards =
          SectionCardIdentityManager.normalizeCollection(cards);
        setSectionCards(normalizedCards);
      } catch (err) {
        setError("Failed to load section cards. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLanguage]);

  const handleCardClick = (card) => {
    if (!card) {
      return;
    }

    const slug =
      card.slug ?? SectionCardIdentityManager.slugFromName(card.section);
    if (!slug) {
      return;
    }
    navigate(`/${slug}`);
  };

  return (
    <Box sx={{ width: "100%", padding: 0 }}>
      {loading ? (
        <Grid container spacing={2}>
          {[...Array(8)].map((_, i) => (
            <SectionCardSkeleton key={i} />
          ))}
        </Grid>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          sx={{
            flexWrap: "wrap",
            pb: { xs: 1, md: 2 },
          }}
        >
          <Grid item xs={12} sm={6} md={4}>
            <DefaultCard sx={{ height: { xs: 220, sm: 240, md: 270 } }} />
          </Grid>
          {sectionCards.slice(0, 8).map((card) => (
            <Grid item xs={6} sm={6} md={4} key={card.id}>
              <SectionCard
                imageSrc={card.image}
                sectionTitle={card.section}
                description={formatDescriptionPreview(
                  card.description,
                  isMobile ? 110 : 150
                )}
                onClick={() => handleCardClick(card)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SectionCardsDisplay;
